import Image from "next/image";
import { urlFor } from "@/sanity/lib/image";

export const PortableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) return null;
      const src = urlFor(value).width(800).url();
      const alt = value.alt || "Blog image";

      if (value.layout === "left") {
        return (
          <div className="my-1 mobile:my-2 sm:my-3 md:my-4 lg:my-5 xl:my-6 float-left mr-6 max-w-[50%]">
            <Image
              src={src}
              alt={alt}
              width={400}
              height={300}
              sizes="(max-width: 768px) 50vw, 400px"
              quality={80}
              placeholder="blur"
              blurDataURL={urlFor(value).width(20).blur(50).url()}
              className="rounded-lg"
            />
            {value.caption && (
              <p className="text-[11px] md:text-xs lg:text-sm 3xl:text-base text-gray-500 mt-1 xl:mt-2 text-left">
                {value.caption}
              </p>
            )}
          </div>
        );
      }

      if (value.layout === "right") {
        return (
          <div className="my-1 mobile:my-2 sm:my-3 md:my-4 lg:my-5 xl:my-6 float-right ml-6 max-w-[50%]">
            <Image
              src={src}
              alt={alt}
              width={400}
              height={300}
              sizes="(max-width: 768px) 50vw, 400px"
              quality={80}
              placeholder="blur"
              blurDataURL={urlFor(value).width(20).blur(50).url()}
              className="rounded-lg"
            />
            {value.caption && (
              <p className="text-[11px] md:text-xs lg:text-sm 3xl:text-base text-gray-500 mt-1 xl:mt-2 text-left">
                {value.caption}
              </p>
            )}
          </div>
        );
      }

      return (
        <div className="my-1 mobile:my-2 sm:my-3 md:my-4 lg:my-5 xl:my-6 flex flex-col items-center mx-1 mobile:mx-12 sm:mx-13 md:mx-14 lg:mx-15 xl:mx-16">
          <div className="w-full max-w-[640px]">
            <Image
              src={src}
              alt={alt}
              width={640}
              height={400}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 640px"
              quality={85}
              placeholder="blur"
              blurDataURL={urlFor(value).width(20).blur(50).url()}
              className="rounded-lg"
            />
            {value.caption && (
              <p className="text-[11px] md:text-xs lg:text-sm 3xl:text-base text-gray-500 mt-1 xl:mt-2 text-left self-start">
                {value.caption}
              </p>
            )}
          </div>
        </div>
      );
    },

    imageGallery: ({ value }) => {
      if (!value?.images?.length) return null;

      const cols = value.layout === "grid3" ? 3 : 2;
      const imgWidth = value.layout === "grid3" ? 300 : 400;
      const imgHeight = value.layout === "grid3" ? 200 : 300;

      return (
        <div
          className={`flex justify-center gap-3 mobile:gap-4 sm:gap-5 md:gap-6 lg:gap-7 xl:gap-8 2xl:gap-10 3xl:gap-16 4xl:gap-18 py-3 sm:py-4 md:py-5 lg:py-6 xl:py-6 2xl:py-7 3xl:py-8 4xl:py-10 `}
        >
          {value.images.slice(0, cols).map((img, idx) =>
            img?.asset?._ref ? (
              <div key={idx} className="flex flex-col">
                <Image
                  key={idx}
                  src={urlFor(img).width(imgWidth).url()}
                  alt={img.alt || "Gallery image"}
                  width={imgWidth}
                  height={imgHeight}
                  sizes={
                    cols === 3
                      ? "(max-width: 768px) 33vw, (max-width: 1200px) 30vw, 400px"
                      : "(max-width: 768px) 50vw, (max-width: 1200px) 40vw, 500px"
                  }
                  quality={85}
                  placeholder="blur"
                  blurDataURL={urlFor(img).width(20).blur(50).url()}
                  className="rounded-lg object-cover"
                />
                {img.caption && (
                  <p className="text-[11px] md:text-xs lg:text-sm 3xl:text-base text-gray-500 mt-1 xl:mt-2 text-left">
                    {img.caption}
                  </p>
                )}
              </div>
            ) : null
          )}
        </div>
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="text-[24px] mobile:text-[22px] sm:text-[24px] md:text-[28px] lg:text-[32px] laptop:text-[35px] 3xl:text-[38px] 4xl:text-[40px]  font-semibold font-alexandria text-foreground my-3 xl:my-4 3xl:my-5 mt-4 xl:mt-5 3xl:mt-6 leading-none tracking-normal capitalize">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-[22px] mobile:text-[20px] sm:text-[22px] md:text-[26px] lg:text-[30px] laptop:text-[32px] 3xl:text-[34px] 4xl:text-[36px]  font-semibold font-alexandria text-foreground my-2 xl:my-3 3xl:my-4 mt-3 xl:mt-4 3xl:mt-5 leading-none tracking-normal capitalize">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-[20px] mobile:text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] laptop:text-[30px] 3xl:text-[32px] 4xl:text-[34px]  font-semibold font-alexandria text-foreground my-1 xl:my-2 3xl:my-3 mt-2 xl:mt-3 3xl:mt-4 leading-none tracking-normal capitalize">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        className="text-[18px] mobile:text-[16px] sm:text-[20px] md:text-[24px] lg:text-[26px] laptop:text-[28px] 3xl:text-[30px] 4xl:text-[32px]  font-semibold font-alexandria text-foreground my-0 xl:my-1 3xl:my-2 mt-1 xl:mt-2 3xl:mt-3 leading-none tracking-normal capitalize"
      >
        {children}
      </h4>
    ),
    normal: ({ children, value }) => {
      const isEmpty = !value?.children?.some((child) => child.text?.trim());
      if (isEmpty) {
        return (
          <p
            aria-hidden="true"
            className="text-sm sm:text-base md:text-lg lg:text-xl laptop:text-xl 3xl:text-2xl 4xl:text-2xl leading-relaxed"
          >
            &nbsp;
          </p>
        );
      }
      return (
        <p className="text-sm sm:text-base md:text-lg lg:text-xl laptop:text-xl 3xl:text-2xl 4xl:text-2xl text-blog font-medium font-manrope my-2 sm:my-3 text-justify leading-relaxed tracking-normal">
          {children}
        </p>
      );
    },
    quote: ({ children }) => (
      <p
        className="font-alexandria text-foreground 
      font-semibold capitalize leading-none tracking-normal
      text-[18px]
      sm:text-[22px]
      md:text-[26px]
      lg:text-[30px]
      laptop:text-[35px]
      2xl:text-[38px]
      3xl:text-[42px]
      4xl:text-[46px]
      my-2 sm:my-4 md:my-6 lg:my-8 xl:my-10 laptop:my-11 3xl:my-12 4xl:my-13"
      >
        {children}
      </p>
    ),
  },
  list: {
    bullet: ({ children, value }) => {
      const level = Math.min(value?.level || 1, 6);
      const bulletTypes = {
        1: "disc",
        2: "circle",
        3: "square",
        4: "disc",
        5: "circle",
        6: "square",
      };

      return (
        <ul
          style={{
            listStyleType: bulletTypes[level] || "disc",
          }}
          className="pl-5 text-blog space-y-1 
          font-medium text-sm sm:text-base md:text-lg lg:text-xl 2xl:text-xl 
          3xl:text-2xl 4xl:text-2xl font-manrope leading-relaxed"
        >
          {children}
        </ul>
      );
    },
    number: ({ children, value }) => {
      const level = value?.level || 1;
      const typeByLevel = { 1: "1", 2: "a", 3: "i", 4: "A", 5: "I" };
      const cssByLevel = {
        1: "decimal",
        2: "lower-alpha",
        3: "lower-roman",
        4: "upper-alpha",
        5: "upper-roman",
      };

      return (
        <ol
          className="pl-5 text-blog space-y-1 
          font-medium text-sm sm:text-base md:text-lg lg:text-xl 2xl:text-xl 
          3xl:text-2xl 4xl:text-2xl font-manrope leading-relaxed"
          type={typeByLevel[level] || "1"}
          style={{
            listStyleType: cssByLevel[level] || "decimal",
          }}
        >
          {children}
        </ol>
      );
    },
  },

  marks: {
    strong: ({ children }) => (
      <strong className="font-bold text-blog text-sm sm:text-base md:text-lg lg:text-xl 2xl:text-xl 3xl:text-2xl 4xl:text-2xl font-manrope leading-relaxed">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-blog/90 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 3xl:text-2xl 4xl:text-2xl font-medium font-manrope leading-relaxed">
        {children}
      </em>
    ),
    link: ({ value, children }) => {
      const target = value?.blank ? "_blank" : "_self";
      const rel = value?.blank ? "noopener noreferrer" : undefined;

      return (
        <a
          href={value?.href}
          target={target}
          rel={rel}
          className="text-[#E93CFC] underline font-medium hover:text-purple-200 text-sm sm:text-base md:text-lg lg:text-xl 2xl:text-xl 3xl:text-2xl 4xl:text-2xl font-manrope leading-relaxed"
        >
          {children}
        </a>
      );
    },
  },
};
